import React from 'react';
import {makeObservable, observable, runInAction} from "mobx";
import {ClientSettingsToggleSectionComponent} from "../../../components/header/top-header-right/client-settings/client-settings-toggle-section.component";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {ServiceBase} from "../../ServiceBase";
import {IClientSettingsSectionRendererModel} from "./section-renderer-model.interface";

export class BiometricLoginRenderer extends ServiceBase implements IClientSettingsSectionRendererModel {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.services.sdk.events.onTouchIDActiveStateChanged(() => this.updateBiometricLoginStatus());

        this.onToggleBiometricLogin = this.onToggleBiometricLogin.bind(this)

        this.updateBiometricLoginStatus();
    }
    @observable.ref
    _isBiometricLoginEnabled = false;

    private async updateBiometricLoginStatus() {
        if(!this.services.device.hybrid.isHybrid) {
            return;
        }
        try {
            const isFeatureOn = await this.services.hybrid.getTouchIDFeaturer().getTokenAndCheckEnable();
            runInAction(() => {
                this._isBiometricLoginEnabled = isFeatureOn;
            });
        } catch (err) {
            this.services.logger.error('Failed to check touch ID feature', err);
        }
    }


    async isAvailable(): Promise<Boolean> {
        if(!this.services.device.hybrid.isHybrid) {
            return false;
        }
        if (this.services.configuration.data?.clientSettings?.biometricLogin?.display
            && this.services.user.isAuthorized) {
            const isDeviceTouchEnabled = await this.services.hybrid.isDeviceTouchEnable();
            const isOpenSettingSupported = await this.services.hybrid.isOpenSettingSupported();
            return isDeviceTouchEnabled || isOpenSettingSupported;
        }

        return false;
    }

    async onToggleBiometricLogin(isToggleOn: boolean) {
        try {
            if (isToggleOn) {
                const isDeviceTouchEnabled = await this.services.hybrid.isDeviceTouchEnable();
                if (isDeviceTouchEnabled) {
                    await this.services.hybrid.getTouchIDFeaturer().getTokenFromSrv();
                }
                else {
                    const isOpenSettingSupported = await this.services.hybrid.isOpenSettingSupported();
                    if (isOpenSettingSupported) {
                        await this.services.hybrid.openTouchIdSettings();
                    }
                    else {
                        this.services.logger.warning('The OS settings cannot be opened.');
                    }
                }
                this._isBiometricLoginEnabled = true;
            } else {
                this.services.hybrid.clearToken();
                this._isBiometricLoginEnabled = false;
            }
        } catch (err) {
            this.services.logger.error('Failed to modify touch ID feature', err);
        }
    };

    render(): React.ReactElement {
        return (<ClientSettingsToggleSectionComponent
            key={this.services.tools.getGuid()}
            icon={this.services.theme.currentTheme.icons.biometricLoginIcon}
            isOn={this._isBiometricLoginEnabled}
            title={this.services.translation.translate('BIOMETRIC_LOGIN')}
            infoTooltipText={this.services.translation.translate('BIOMETRIC_LOGIN_INFO')}
            onToggle={this.onToggleBiometricLogin}
        />)
    }
}